import React from "react";
import featureBg from "../../images/bg/features-gradient-bg.png";
import BrandSection from "./BrandSection";

// ─── Inline icon components ───────────────────────────────────────────────────
const IconLock = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
    <rect x="5" y="11" width="14" height="10" rx="2" stroke="#00c896" strokeWidth="1.8"/>
    <path d="M8 11V7a4 4 0 0 1 8 0v4" stroke="#00c896" strokeWidth="1.8" strokeLinecap="round"/>
    <circle cx="12" cy="16" r="1.2" fill="#00c896"/>
  </svg>
);
const IconShield = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
    <path d="M12 3L4 7v5c0 4.5 3.4 8.7 8 9.9 4.6-1.2 8-5.4 8-9.9V7L12 3z" stroke="#00c896" strokeWidth="1.8" strokeLinejoin="round"/>
    <path d="M9 12l2 2 4-4" stroke="#00c896" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const IconCompliance = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
    <rect x="4" y="3" width="16" height="18" rx="2" stroke="#00c896" strokeWidth="1.8"/>
    <path d="M8 8h8M8 12h8M8 16h5" stroke="#00c896" strokeWidth="1.8" strokeLinecap="round"/>
  </svg>
);
const IconApi = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
    <path d="M7 8l-4 4 4 4M17 8l4 4-4 4" stroke="#00c896" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M14 4l-4 16" stroke="#00c896" strokeWidth="1.8" strokeLinecap="round"/>
  </svg>
);
const IconBolt = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
    <path d="M13 2L4.5 13.5H12L11 22l8.5-11.5H12L13 2z" stroke="#00c896" strokeWidth="1.8" strokeLinejoin="round"/>
  </svg>
);
const IconUptime = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="9" stroke="#00c896" strokeWidth="1.8"/>
    <path d="M12 7v5l3 3" stroke="#00c896" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

// ─── Center animated visualization ────────────────────────────────────────────
const SecurityCore: React.FC = () => (
  <div style={{ position: "relative", width: "100%", maxWidth: "320px", margin: "0 auto", aspectRatio: "1" }}>
    <style>{`
      @keyframes spin-slow  { from { transform: rotate(0deg);   } to { transform: rotate(360deg);  } }
      @keyframes spin-rev   { from { transform: rotate(0deg);   } to { transform: rotate(-360deg); } }
      @keyframes pulse-ring { 0%,100% { opacity:.18; transform:scale(1);   }
                              50%     { opacity:.45; transform:scale(1.06); } }
      @keyframes float-badge { 0%,100%{transform:translateY(0);}
                               50%{transform:translateY(-6px);} }
      .sc-ring1 { animation: spin-slow 18s linear infinite; }
      .sc-ring2 { animation: spin-rev  12s linear infinite; }
      .sc-pulse1 { animation: pulse-ring 3s ease-in-out infinite; }
      .sc-pulse2 { animation: pulse-ring 3s ease-in-out infinite 1.5s; }
      .sc-badge  { animation: float-badge 4s ease-in-out infinite; }
      .sc-badge2 { animation: float-badge 4s ease-in-out infinite 2s; }
    `}</style>

    <svg viewBox="0 0 320 320" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ width: "100%", height: "100%" }}>
      <defs>
        <linearGradient id="gTeal" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#00c896"/>
          <stop offset="100%" stopColor="#3b82f6"/>
        </linearGradient>
        <linearGradient id="gBlue" x1="1" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#3b82f6"/>
          <stop offset="100%" stopColor="#00c896"/>
        </linearGradient>
        <filter id="glow">
          <feGaussianBlur stdDeviation="6" result="blur"/>
          <feMerge><feMergeNode in="blur"/><feMergeNode in="SourceGraphic"/></feMerge>
        </filter>
      </defs>

      {/* Pulse rings */}
      <circle className="sc-pulse1" cx="160" cy="160" r="130" stroke="url(#gTeal)" strokeWidth="1" strokeDasharray="6 4" fill="none"/>
      <circle className="sc-pulse2" cx="160" cy="160" r="108" stroke="url(#gBlue)" strokeWidth="1" strokeDasharray="4 6" fill="none"/>

      {/* Spinning orbit ring */}
      <g className="sc-ring1" style={{ transformOrigin: "160px 160px" }}>
        <circle cx="160" cy="160" r="88" stroke="rgba(0,200,150,0.18)" strokeWidth="1.5" strokeDasharray="8 5" fill="none"/>
        {/* Orbit dot */}
        <circle cx="160" cy="72" r="5" fill="#00c896" filter="url(#glow)"/>
      </g>

      {/* Spinning orbit ring 2 */}
      <g className="sc-ring2" style={{ transformOrigin: "160px 160px" }}>
        <ellipse cx="160" cy="160" rx="88" ry="44" stroke="rgba(59,130,246,0.25)" strokeWidth="1.5" strokeDasharray="6 6" fill="none"/>
        <circle cx="248" cy="160" r="4" fill="#3b82f6" filter="url(#glow)"/>
      </g>

      {/* Center shield body */}
      <circle cx="160" cy="160" r="62" fill="rgba(0,200,150,0.07)" stroke="rgba(0,200,150,0.2)" strokeWidth="1.5"/>
      <circle cx="160" cy="160" r="50" fill="#ffffff" stroke="rgba(0,200,150,0.15)" strokeWidth="1"/>

      {/* Shield icon */}
      <path d="M160 128 L136 138.4V158c0 13.5 10.2 26.1 24 29.7 13.8-3.6 24-16.2 24-29.7V138.4L160 128z"
        fill="url(#gTeal)" opacity="0.15"/>
      <path d="M160 128 L136 138.4V158c0 13.5 10.2 26.1 24 29.7 13.8-3.6 24-16.2 24-29.7V138.4L160 128z"
        stroke="url(#gTeal)" strokeWidth="2" strokeLinejoin="round" fill="none"/>
      {/* Shield checkmark */}
      <path d="M152 158 l5.5 5.5 10.5-10.5" stroke="#00c896" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>

      {/* Step badges — floating */}
      <g className="sc-badge">
        {/* eKYC badge top-left */}
        <rect x="14" y="60" width="68" height="28" rx="8" fill="#ffffff" stroke="rgba(0,200,150,0.25)" strokeWidth="1"/>
        <circle cx="28" cy="74" r="6" fill="rgba(0,200,150,0.12)"/>
        <path d="M25 74l2 2 4-4" stroke="#00c896" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        <text x="38" y="78" fontSize="9" fill="#0f2040" fontWeight="600" fontFamily="system-ui,sans-serif">eKYC ✓</text>
      </g>

      <g className="sc-badge2">
        {/* Liveness badge bottom-right */}
        <rect x="238" y="228" width="72" height="28" rx="8" fill="#ffffff" stroke="rgba(59,130,246,0.25)" strokeWidth="1"/>
        <circle cx="252" cy="242" r="6" fill="rgba(59,130,246,0.12)"/>
        <path d="M249 242l2 2 4-4" stroke="#3b82f6" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        <text x="262" y="246" fontSize="9" fill="#0f2040" fontWeight="600" fontFamily="system-ui,sans-serif">Live ✓</text>
      </g>

      {/* AES label bottom-left */}
      <g className="sc-badge">
        <rect x="6" y="228" width="78" height="28" rx="8" fill="#ffffff" stroke="rgba(0,200,150,0.2)" strokeWidth="1"/>
        <text x="16" y="238" fontSize="8" fill="#6b7280" fontFamily="system-ui,sans-serif">Encrypted</text>
        <text x="16" y="250" fontSize="8.5" fill="#0f2040" fontWeight="700" fontFamily="system-ui,sans-serif">AES-256</text>
      </g>

      {/* 99.9% label top-right */}
      <g className="sc-badge2">
        <rect x="238" y="56" width="72" height="28" rx="8" fill="#ffffff" stroke="rgba(59,130,246,0.2)" strokeWidth="1"/>
        <text x="248" y="66" fontSize="8" fill="#6b7280" fontFamily="system-ui,sans-serif">Uptime</text>
        <text x="248" y="78" fontSize="8.5" fill="#0f2040" fontWeight="700" fontFamily="system-ui,sans-serif">99.9% SLA</text>
      </g>
    </svg>
  </div>
);

// ─── Feature cards data ────────────────────────────────────────────────────────
const leftFeatures = [
  { icon: <IconLock />,        title: <>AES-256 &amp; <br />TLS 1.3 encryption</> },
  { icon: <IconShield />,      title: <>ISO 27001 &amp; <br />SOC 2 Type II certified</> },
  { icon: <IconCompliance />,  title: <>GDPR, PDPA &amp; <br />OJK compliant</> },
];
const rightFeatures = [
  { icon: <IconApi />,    title: <>REST API integration <br />in under 1 hour</> },
  { icon: <IconBolt />,   title: <>Real-time results <br />in under 3 seconds</> },
  { icon: <IconUptime />, title: <>99.9% uptime <br />SLA guarantee</> },
];

const FeaturesAndBrands: React.FC = () => {
  return (
    <div
      className="bg_img"
      style={{
        background: `url(${featureBg}) no-repeat center center`,
        backgroundSize: "cover",
      }}
    >
      <section className="feature pt-145">
        <div className="container">
          <div className="sec-title sec-title-center fea-sec-title text-center mb-50">
            <span className="sub-title">Why TrustEngine</span>
            <h2 className="title">
              Security{" "}
              <span style={{
                display: "inline-flex", alignItems: "center", justifyContent: "center",
                width: "36px", height: "36px", borderRadius: "50%",
                background: "linear-gradient(135deg,#00c896,#3b82f6)",
                verticalAlign: "middle", margin: "0 6px",
              }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path d="M12 3L4 7v5c0 4.5 3.4 8.7 8 9.9 4.6-1.2 8-5.4 8-9.9V7L12 3z" fill="white"/>
                </svg>
              </span>{" "}
              built into every check
            </h2>
          </div>

          <div className="row mt-none-30 align-items-center">
            {/* Left Features */}
            <div className="col-lg-4 col-md-6 mt-30">
              <div className="xb-feature-left-item">
                {leftFeatures.map((f, i) => (
                  <div key={i}
                    className="xb-feature-item xb-feature-item2 wow fadeInUp"
                    data-wow-delay={`${(i + 1) * 100}ms`}
                    data-wow-duration="600ms"
                  >
                    <div className="xb-item--inner justify-content-end xb-border" style={{
                      background: "#ffffff",
                      border: "1px solid rgba(15,32,64,0.08)",
                      borderRadius: "14px",
                      boxShadow: "0 2px 12px rgba(15,32,64,0.05)",
                    }}>
                      <h2 className="xb-item--title text-end" style={{ color: "#0f2040", fontSize: "14px", fontWeight: 600 }}>
                        {f.title}
                      </h2>
                      <span className="xb-item--icon" style={{
                        width: "44px", height: "44px", borderRadius: "12px",
                        background: "rgba(0,200,150,0.08)",
                        border: "1px solid rgba(0,200,150,0.2)",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        flexShrink: 0,
                      }}>
                        {f.icon}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Center visualization */}
            <div className="col-lg-4 col-md-12 mt-30 d-flex justify-content-center align-items-center">
              <SecurityCore />
            </div>

            {/* Right Features */}
            <div className="col-lg-4 col-md-6 order-lg-last mt-30">
              <div className="xb-feature-right-item">
                {rightFeatures.map((f, i) => (
                  <div key={i}
                    className="xb-feature-item xb-feature-item2 wow fadeInUp"
                    data-wow-delay={`${(i + 1) * 100}ms`}
                    data-wow-duration="600ms"
                  >
                    <div className="xb-item--inner xb-border" style={{
                      background: "#ffffff",
                      border: "1px solid rgba(15,32,64,0.08)",
                      borderRadius: "14px",
                      boxShadow: "0 2px 12px rgba(15,32,64,0.05)",
                    }}>
                      <span className="xb-item--icon" style={{
                        width: "44px", height: "44px", borderRadius: "12px",
                        background: "rgba(0,200,150,0.08)",
                        border: "1px solid rgba(0,200,150,0.2)",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        flexShrink: 0,
                      }}>
                        {f.icon}
                      </span>
                      <h2 className="xb-item--title" style={{ color: "#0f2040", fontSize: "14px", fontWeight: 600 }}>
                        {f.title}
                      </h2>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <BrandSection />
    </div>
  );
};

export default FeaturesAndBrands;
